import React from "react";
import { Typography, List, ListItem, Container, Box } from "@mui/material";
import CustomHelmet from "../../common/CustomHelmet";

const MarriageCounseling = () => {
  return (
    <>
      <CustomHelmet
        title="Marriage and Relationship Counseling - Dr. Anne Rothenberg"
        description="Dr. Anne Rothenberg offers expert marriage and relationship counseling to help couples overcome challenges, improve communication, and strengthen their bond."
        keywords="marriage counseling, relationship counseling, couples therapy, communication improvement, intimacy issues, conflict resolution, Dr. Anne Rothenberg, Maitland FL"
        ogTitle="Marriage and Relationship Counseling - Dr. Anne Rothenberg"
        ogDescription="Expert marriage and relationship counseling to help couples overcome challenges, improve communication, and strengthen their bond."
        ogUrl="https://drannerothenberg.com/services/marriage-counseling"
      />
      <Container maxWidth="md">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
            textAlign: "left",
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          <Typography variant="h4" gutterBottom>
            Marriage and Relationship Counseling
          </Typography>

          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ color: "#555", mb: 3 }}
          >
            Dr. Anne Rothenberg specializes in helping couples navigate the
            complex challenges of modern relationships. As an experienced
            relationship and sex therapist, she provides a safe, non-judgmental
            space where partners can openly discuss their most intimate concerns
            and work toward building a stronger, more satisfying connection.
          </Typography>

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#4A90E2", mt: 3 }}
          >
            "We Don't Talk Anymore" - Communication Breakdown
          </Typography>
          <List
            dense
            sx={{
              listStyleType: "disc",
              pl: 4,
              color: "#333",
              textAlign: "left",
              mb: 3,
            }}
          >
            <ListItem sx={{ display: "list-item" }}>
              "We're like roommates living separate lives"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Everything turns into a fight - even small things"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I walk on eggshells around my partner"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "We have the same argument over and over"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "My partner shuts down when I try to talk"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I feel criticized no matter what I do"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "We can't discuss anything without it becoming an attack"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I don't feel heard or understood"
            </ListItem>
          </List>

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#4A90E2", mt: 3 }}
          >
            Sexual and Intimacy Struggles
          </Typography>
          <List
            dense
            sx={{
              listStyleType: "disc",
              pl: 4,
              color: "#333",
              textAlign: "left",
              mb: 3,
            }}
          >
            <ListItem sx={{ display: "list-item" }}>
              "We haven't had sex in months (or years)"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "My partner wants sex all the time and I never do"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I'm always the one initiating - it's exhausting"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Sex feels like a chore on my to-do list"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I can't orgasm with my partner"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "We had a baby and our sex life disappeared"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I have pain during sex but I'm afraid to tell my partner"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "My body has changed and I hate being touched"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I'm not attracted to my partner anymore"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "We're sexually incompatible - different desires and needs"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I think about other people during sex"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Performance anxiety is ruining our intimacy"
            </ListItem>
          </List>

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#4A90E2", mt: 3 }}
          >
            Betrayal and Broken Trust
          </Typography>
          <List
            dense
            sx={{
              listStyleType: "disc",
              pl: 4,
              color: "#333",
              textAlign: "left",
              mb: 3,
            }}
          >
            <ListItem sx={{ display: "list-item" }}>
              "I discovered my partner's affair"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "They're emotionally involved with someone else"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I caught them sexting/on dating apps"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "They lied about money/debt/spending"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I can't stop checking their phone"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "How do I know if I can trust them again?"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "They had an emotional affair at work"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I'm attracted to someone else and feel guilty"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Porn use has become a problem in our relationship"
            </ListItem>
          </List>

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#4A90E2", mt: 3 }}
          >
            Life Transitions and External Stressors
          </Typography>
          <List
            dense
            sx={{
              listStyleType: "disc",
              pl: 4,
              color: "#333",
              textAlign: "left",
              mb: 3,
            }}
          >
            <ListItem sx={{ display: "list-item" }}>
              "We're struggling with infertility"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "A miscarriage has driven us apart"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "We can't agree on having kids"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Blending our families is harder than we thought"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Job loss has put strain on everything"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "We're dealing with aging parents"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Empty nest - now what?"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Health issues have changed our dynamic"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "COVID changed everything about our relationship"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Retirement isn't what we expected"
            </ListItem>
          </List>

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#4A90E2", mt: 3 }}
          >
            Emotional Distance and Disconnection
          </Typography>
          <List
            dense
            sx={{
              listStyleType: "disc",
              pl: 4,
              color: "#333",
              textAlign: "left",
              mb: 3,
            }}
          >
            <ListItem sx={{ display: "list-item" }}>
              "I feel lonely even when we're together"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "We're living like business partners"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I don't think I'm in love anymore"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "My partner doesn't seem to need me"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "We never laugh together anymore"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I feel taken for granted"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "We've grown into different people"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "There's no emotional intimacy left"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "I don't feel like a priority"
            </ListItem>
          </List>

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#4A90E2", mt: 3 }}
          >
            Mental Health and Personal Struggles
          </Typography>
          <List
            dense
            sx={{
              listStyleType: "disc",
              pl: 4,
              color: "#333",
              textAlign: "left",
              mb: 3,
            }}
          >
            <ListItem sx={{ display: "list-item" }}>
              "My partner's anxiety/depression affects us both"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Past trauma is impacting our intimacy"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Addiction is destroying our relationship"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Body image issues are affecting our sex life"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "ADHD makes relationship maintenance hard"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "My partner won't get help for their issues"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Medication has killed my sex drive"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Work stress is consuming our relationship"
            </ListItem>
          </List>

          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#4A90E2", mt: 3 }}
          >
            Questions About the Future
          </Typography>
          <List
            dense
            sx={{
              listStyleType: "disc",
              pl: 4,
              color: "#333",
              textAlign: "left",
              mb: 3,
            }}
          >
            <ListItem sx={{ display: "list-item" }}>
              "Should we stay together for the kids?"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Is this relationship worth saving?"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "How do I know when it's time to leave?"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Can we fall back in love?"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Are we better as friends?"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Should we open our relationship?"
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              "Is couples therapy our last hope?"
            </ListItem>
          </List>

          <Typography
            variant="body1"
            sx={{ mt: 4, fontStyle: "italic", color: "#666" }}
          >
            If any of these concerns resonate with you, know that you're not
            alone. Dr. Rothenberg has helped countless couples work through
            these exact issues. With compassion, expertise, and evidence-based
            approaches, she can help you and your partner find your way back to
            each other - or make decisions about your future with clarity and
            confidence.
          </Typography>

          <Typography
            variant="body1"
            sx={{ mt: 3, fontWeight: "bold", color: "#4A90E2" }}
          >
            Remember: Seeking help is a sign of strength, not weakness. The fact
            that you're here shows you care about your relationship.
          </Typography>
        </Box>
      </Container>
    </>
  );
};

export default MarriageCounseling;
